import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Users, ArrowLeft, AlertTriangle, TrendingUp, Target, Plus, 
  ChevronRight, Clock, CheckCircle, RefreshCw, BarChart3, Mail
} from "lucide-react";
import { API_BASE_URL } from "../../lib/config";

const API_URL = API_BASE_URL;

const ClassDetail = () => {
  const { classId } = useParams();
  const navigate = useNavigate();
  const [classData, setClassData] = useState(null);
  const [roster, setRoster] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("roster");
  const [showAddStudents, setShowAddStudents] = useState(false);
  const [studentEmails, setStudentEmails] = useState("");
  const [addingStudents, setAddingStudents] = useState(false);

  const getTeacherToken = () => localStorage.getItem("teacherToken");

  useEffect(() => {
    fetchClassData();
  }, [classId]);

  const fetchClassData = async () => {
    const token = getTeacherToken();
    if (!token) {
      navigate("/teacher/login");
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [classRes, rosterRes, analyticsRes] = await Promise.all([
        fetch(`${API_URL}/teacher/classes/${classId}`, { headers }),
        fetch(`${API_URL}/teacher/classes/${classId}/roster`, { headers }),
        fetch(`${API_URL}/teacher/classes/${classId}/analytics`, { headers })
      ]);

      if (classRes.ok) setClassData(await classRes.json());
      if (rosterRes.ok) setRoster(await rosterRes.json());
      if (analyticsRes.ok) setAnalytics(await analyticsRes.json());
    } catch (error) {
      console.error("Error fetching class data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddStudents = async () => {
    const emails = studentEmails.split(/[\n,]/).map(e => e.trim()).filter(e => e);
    if (emails.length === 0) return;

    setAddingStudents(true);
    try {
      const token = getTeacherToken();
      const res = await fetch(`${API_URL}/teacher/classes/${classId}/students`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ emails, class_id: classId })
      });

      if (res.ok) {
        const data = await res.json();
        alert(`Added ${data.added.length} students, invited ${data.invited.length} new students`);
        setShowAddStudents(false);
        setStudentEmails("");
        fetchClassData();
      }
    } catch (error) {
      console.error("Error adding students:", error);
    } finally {
      setAddingStudents(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!classData) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-500 mb-4">Class not found</p>
          <Link to="/teacher/dashboard" className="text-indigo-600 hover:underline">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white py-6">
        <div className="max-w-6xl mx-auto px-4">
          <Link to="/teacher/dashboard" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading font-bold text-2xl">{classData.name}</h1>
              <p className="text-indigo-200">Grade {classData.grade_level} • {roster?.total_students || 0} students</p>
            </div>
            <button
              onClick={() => setShowAddStudents(true)}
              className="px-4 py-2 bg-lime-500 text-white font-medium rounded-lg hover:bg-lime-600 transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Students
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Quick Stats */}
        <div className="grid sm:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 border-2 border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-slate-500 text-sm">Students</p>
                <p className="font-bold text-xl text-slate-800">{roster?.total_students || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border-2 border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-lime-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-lime-600" />
              </div>
              <div>
                <p className="text-slate-500 text-sm">Avg Accuracy</p>
                <p className="font-bold text-xl text-slate-800">{analytics?.avg_accuracy || 0}%</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border-2 border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Target className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-slate-500 text-sm">Questions</p>
                <p className="font-bold text-xl text-slate-800">{analytics?.total_questions_answered || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border-2 border-red-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-slate-500 text-sm">At Risk</p>
                <p className="font-bold text-xl text-red-600">{roster?.at_risk_count || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {["roster", "analytics", "weak-topics"].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === tab
                  ? "bg-indigo-600 text-white"
                  : "bg-white text-slate-600 hover:bg-slate-100"
              }`}
            >
              {tab === "roster" ? "Student Roster" : tab === "analytics" ? "Class Analytics" : "Weak Topics"}
            </button>
          ))}
        </div>

        {/* Roster Tab */}
        {activeTab === "roster" && (
          <div className="space-y-4">
            {/* At-Risk Alert */}
            {roster?.at_risk?.length > 0 && (
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <h3 className="font-bold text-red-800">Students Needing Attention ({roster.at_risk.length})</h3>
                </div>
                <div className="space-y-2">
                  {roster.at_risk.map(student => (
                    <div key={student.student_id} className="flex items-center justify-between bg-white rounded-lg p-3">
                      <div>
                        <p className="font-medium text-slate-800">{student.student_name}</p>
                        <p className="text-sm text-red-600">{student.risk_reason}</p>
                      </div>
                      <Link
                        to={`/teacher/reteach?student=${student.student_id}&topics=${student.weak_topics.join(',')}`}
                        className="px-3 py-1 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200"
                      >
                        Create Reteach Pack
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Full Roster */}
            <div className="bg-white rounded-2xl border-2 border-slate-100 overflow-hidden">
              <div className="p-4 border-b border-slate-100">
                <h3 className="font-bold text-slate-800">All Students</h3>
              </div>
              <div className="divide-y divide-slate-50">
                {roster?.students?.length === 0 ? (
                  <div className="p-8 text-center">
                    <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No students yet</p>
                    <button
                      onClick={() => setShowAddStudents(true)}
                      className="text-indigo-600 font-medium hover:underline mt-2"
                    >
                      Add your first students
                    </button>
                  </div>
                ) : (
                  roster?.students?.map(student => (
                    <div key={student.student_id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                          student.is_at_risk ? "bg-red-100 text-red-600" : "bg-indigo-100 text-indigo-600"
                        }`}>
                          {student.student_name.charAt(0)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-800">{student.student_name}</p>
                            {student.is_at_risk && (
                              <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded-full">
                                AT RISK
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-500">{student.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6 text-sm">
                        <div className="text-center">
                          <p className="font-bold text-slate-800">{student.accuracy}%</p>
                          <p className="text-slate-400">Accuracy</p>
                        </div>
                        <div className="text-center">
                          <p className="font-bold text-slate-800">{student.total_questions}</p>
                          <p className="text-slate-400">Questions</p>
                        </div>
                        <div className="text-center">
                          <p className="font-bold text-slate-800">{student.streak}</p>
                          <p className="text-slate-400">Streak</p>
                        </div>
                        <div className="text-center">
                          <p className={`font-bold ${student.days_inactive > 3 ? "text-red-600" : "text-slate-800"}`}>
                            {student.days_inactive}d
                          </p>
                          <p className="text-slate-400">Inactive</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === "analytics" && analytics && (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl border-2 border-slate-100 p-6">
              <h3 className="font-bold text-slate-800 mb-4">Class Performance</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Total Questions Answered</span>
                  <span className="font-bold text-slate-800">{analytics.total_questions_answered}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Average Accuracy</span>
                  <span className="font-bold text-lime-600">{analytics.avg_accuracy}%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Active Students</span>
                  <span className="font-bold text-slate-800">{analytics.active_students} / {analytics.total_students}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">At-Risk Students</span>
                  <span className="font-bold text-red-600">{analytics.at_risk_count}</span>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl border-2 border-slate-100 p-6">
              <h3 className="font-bold text-slate-800 mb-4">Assignment Status</h3>
              <div className="flex items-center justify-center h-32">
                <p className="text-slate-400">Coming soon...</p>
              </div>
            </div>
          </div>
        )}

        {/* Weak Topics Tab */}
        {activeTab === "weak-topics" && analytics && (
          <div className="bg-white rounded-2xl border-2 border-slate-100 overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-bold text-slate-800">Topics Needing Reteach</h3>
              <span className="text-sm text-slate-500">Sorted by struggle rate</span>
            </div>
            {analytics.weak_topics?.length === 0 ? (
              <div className="p-8 text-center">
                <CheckCircle className="w-12 h-12 text-lime-500 mx-auto mb-3" />
                <p className="text-slate-600 font-medium">No struggling topics detected!</p>
                <p className="text-slate-400 text-sm">Your class is doing great.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-50">
                {analytics.weak_topics?.map((topic, i) => (
                  <div key={i} className="p-4 flex items-center justify-between hover:bg-slate-50">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        topic.struggle_rate > 60 ? "bg-red-100" : "bg-orange-100"
                      }`}>
                        <AlertTriangle className={`w-5 h-5 ${
                          topic.struggle_rate > 60 ? "text-red-600" : "text-orange-600"
                        }`} />
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">{topic.topic}</p>
                        <p className="text-sm text-slate-500">{topic.total_attempts} attempts across class</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className={`font-bold ${topic.struggle_rate > 60 ? "text-red-600" : "text-orange-600"}`}>
                          {topic.struggle_rate}%
                        </p>
                        <p className="text-xs text-slate-400">struggle rate</p>
                      </div>
                      <Link
                        to={`/teacher/reteach?topic=${encodeURIComponent(topic.topic)}`}
                        className="px-3 py-2 bg-indigo-100 text-indigo-700 rounded-lg font-medium hover:bg-indigo-200 flex items-center gap-1"
                      >
                        <RefreshCw className="w-4 h-4" />
                        Reteach Pack
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Add Students Modal */}
      {showAddStudents && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h2 className="font-heading font-bold text-xl text-slate-800 mb-4">Add Students</h2>
            <p className="text-slate-600 text-sm mb-4">
              Enter student email addresses (one per line or comma-separated). Existing students will be added directly, new students will receive an invitation.
            </p>
            <textarea
              value={studentEmails}
              onChange={(e) => setStudentEmails(e.target.value)}
              placeholder="student1@school.edu&#10;student2@school.edu"
              className="w-full h-40 p-3 border-2 border-slate-200 rounded-xl focus:border-indigo-500 focus:ring-0 resize-none"
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setShowAddStudents(false)}
                className="flex-1 py-2 bg-slate-100 text-slate-700 font-medium rounded-lg hover:bg-slate-200"
              >
                Cancel
              </button>
              <button
                onClick={handleAddStudents}
                disabled={addingStudents}
                className="flex-1 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {addingStudents ? "Adding..." : "Add Students"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassDetail;
